import React from 'react';

import type { GalleryImageWithTitle } from 'Components/Gallery/MainGallery/MainGallery';
import { useGalleryTrigger } from 'Components/pages/VehicleDetailsRedesign/Components/GalleryProvider';
import BasicThumbnail from 'Components/Thumbnail/BasicThumbnail';

import { formatDamageMeta } from '../../helpers/formatDamageMeta';

import { useInlineSvg, useOriginalSize } from './useInlineSvg';

import styles from './DamageCard.module.scss';

const MARKER_R = 6;

interface DamageCardProps {
	bodyType?: string;
	image: GalleryImageWithTitle;
	overlayUrl?: string;
	showOverlay?: boolean;
}

const formatPanelName = (panelType: string): string => panelType.replace(/_/g, ' ');

export const DamageCard: React.FC<DamageCardProps> = ({
	bodyType,
	image,
	overlayUrl: overlayUrlProp,
	showOverlay = false,
}) => {
	const openGallery = useGalleryTrigger();
	const svgData = useInlineSvg(showOverlay ? overlayUrlProp : undefined);
	const origSize = useOriginalSize(overlayUrlProp);

	const damageMeta = 'damageMeta' in image ? image.damageMeta : null;
	const description = damageMeta ? formatDamageMeta(damageMeta) : '';
	const panelType = damageMeta?.panelType;
	const resolvedOverlayUrl = overlayUrlProp;
	const hasOverlayData = showOverlay && damageMeta && resolvedOverlayUrl && damageMeta.side;
	const hasXY = damageMeta?.x != null && damageMeta?.y != null;

	// Build the SVG element ID for the active panel: {bodyType}-{side}-{panelType}
	const activePanelId =
		bodyType && damageMeta?.side && panelType ? `${bodyType.toLowerCase()}-${damageMeta.side}-${panelType}` : undefined;

	// Scale marker from filename dimensions (origSize) to SVG element dimensions (svgData.width/height)
	// x/y are pixel coords in filename-dimension space (e.g. 1266x464)
	// SVG viewBox uses element dimensions (e.g. 644x236)
	let markerCx: number | null = null;
	let markerCy: number | null = null;

	if (hasXY && svgData && origSize && origSize.origW > 0 && origSize.origH > 0) {
		markerCx = (Number(damageMeta!.x) * svgData.width) / origSize.origW;
		markerCy = (Number(damageMeta!.y) * svgData.height) / origSize.origH;
	}

	const handleClick = () => {
		openGallery({ id: image.id });
	};

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			handleClick();
		}
	};

	const kindLabel = (image.kind || 'damage').replace(/_/g, ' ');

	return (
		<div className={styles.card} data-testid="damage-card">
			<div className={styles.panelName} data-testid="damage-card-panel-name">
				{hasOverlayData && panelType ? formatPanelName(panelType) : '\u00A0'}
			</div>
			<div className={styles.description} data-testid="damage-card-description">
				{description || '\u00A0'}
			</div>
			<div className={styles.wireframe}>
				{hasOverlayData && svgData ? (
					<svg
						aria-label={`${panelType ? formatPanelName(panelType) : 'Damage'} wireframe`}
						className={styles.svgOverlay}
						fill="transparent"
						onClick={handleClick}
						onKeyDown={handleKeyDown}
						role="img"
						tabIndex={0}
						viewBox={svgData.viewBox}
						xmlns="http://www.w3.org/2000/svg"
					>
						<g dangerouslySetInnerHTML={{ __html: svgData.inner }} />
						{activePanelId && <use className={styles.activePanel} href={`#${activePanelId}`} />}
						{markerCx != null && markerCy != null && (
							<circle
								className={styles.markerCircle}
								cx={markerCx}
								cy={markerCy}
								data-testid="damage-card-marker"
								r={MARKER_R}
							/>
						)}
					</svg>
				) : (
					<div className={styles.placeholder} data-testid="damage-card-wireframe-placeholder">
						<span className={styles.placeholderText}>No wireframe</span>
					</div>
				)}
			</div>
			<div className={styles.details}>
				{image.url ? (
					<BasicThumbnail
						authenticated
						alt={kindLabel}
						className={styles.thumb}
						height={279}
						image={{
							presets: image.presetsArray,
							url: image.url,
						}}
						imgAttributes={{ 'aria-label': `${kindLabel} photo` }}
						onClick={handleClick}
						rootComponent="DamageCard"
						width={372}
					/>
				) : (
					<div className={styles.photoPlaceholder} data-testid="damage-card-photo-placeholder">
						<span className={styles.placeholderText}>No photo</span>
					</div>
				)}
			</div>
		</div>
	);
};
